import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/hooks/use-language';
import { toast } from 'sonner';

const SKIP_KEY = 'usernameSetupSkipped';

const suggestFromMetadata = (meta: Record<string, any> | undefined): string => {
  const raw = (meta?.full_name || meta?.name || '').toString().trim();
  if (!raw) return '';
  const base = raw
    .replace(/[“”"']/g, '')
    .split(/\s+/)[0]
    .replace(/[^\p{L}\p{N}_-]/gu, '');
  return base.slice(0, 20);
};

export const UsernameSetupDialog: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const u = (t as any).usernameSetup;

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      setOpen(false);
      return;
    }
    const existing = (user.user_metadata?.username || '').trim();
    const needsUsername = !existing || existing.includes('@');
    if (!needsUsername) return;
    if (localStorage.getItem(`${SKIP_KEY}:${user.id}`)) return;

    setValue(suggestFromMetadata(user.user_metadata));
    setOpen(true);
  }, [user]);

  const handleSkip = () => {
    if (user) localStorage.setItem(`${SKIP_KEY}:${user.id}`, '1');
    setOpen(false);
  };

  const handleSave = async () => {
    const name = value.trim();
    if (name.length < 2) {
      toast.error(u.tooShort);
      return;
    }
    if (name.includes('@')) {
      toast.error(u.noEmail);
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ data: { username: name } });
      if (error) throw error;

      // Backfill the author name on the user's previous, non-anonymous reports.
      if (user) {
        await supabase
          .from('reports')
          .update({ created_by: name })
          .eq('user_id', user.id)
          .or('created_by.is.null,created_by.eq.')
          .is('deleted_at', null);
      }

      toast.success(u.success);
      setOpen(false);
    } catch (err: any) {
      console.error('Username setup failed:', err);
      toast.error(u.error);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleSkip(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{u.title}</DialogTitle>
          <DialogDescription>{u.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={u.placeholder}
            maxLength={24}
            autoFocus
          />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={handleSkip} disabled={saving}>
              {u.skip}
            </Button>
            <Button className="flex-1" onClick={handleSave} disabled={saving}>
              {saving ? u.saving : u.save}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UsernameSetupDialog;
